import { CameraControlsImpl } from "@react-three/drei";
import { CanvasTexture, Mesh, SRGBColorSpace } from "three";
import { BoxGeometry, MeshBasicMaterial } from "three";
import { Box3, Sphere, Vector3 } from "three";
import { radToDeg } from "three/src/math/MathUtils.js";

export const snapToGrid = (value, gridSize = 1) => {
  return Math.round(value / gridSize) * gridSize;
};

export function clampToRoomMesh(pointerPos, decorWidth, decorHeight, roomMesh) {
  // Get room bounds
  const roomBox = new Box3().setFromObject(roomMesh);

  // Half-size of the plane
  const hw = decorWidth / 2;
  const hh = decorHeight / 2;

  // Plane corners in local space
  const localCorners = [
    new Vector3(-hw, -hh, 0),
    new Vector3(hw, -hh, 0),
    new Vector3(hw, hh, 0),
    new Vector3(-hw, hh, 0),
  ];

  // Temp vars
  const worldCorner = new Vector3();
  const adjustedPos = pointerPos.clone();

  // Check all corners
  for (const corner of localCorners) {
    worldCorner.copy(corner).add(pointerPos); // assuming no rotation
    // Clamp each axis to room bounds
    adjustedPos.x = Math.max(
      roomBox.min.x + hw,
      Math.min(roomBox.max.x - hw, adjustedPos.x)
    );
    adjustedPos.y = Math.max(
      roomBox.min.y + hh,
      Math.min(roomBox.max.y - hh, adjustedPos.y)
    );
  }

  return adjustedPos;
}

export function isPointValidForRoomMesh(
  point,
  width,
  height,
  normal,
  roomMesh
) {
  const roomBox = new Box3().setFromObject(roomMesh);

  if (point.y + height / 2 > roomBox.max.y - roomBox.min.y - 0.01) {
    return false;
  }
  if (point.y - height / 2 < roomBox.min.y + 0.01) {
    return false;
  }
  if (normal.z) {
    if (
      Math.abs(point.x + Math.sign(point.x) * (width / 2)) >
      Math.abs(roomBox.max.x)
    ) {
      return false;
    }
  } else if (normal.x) {
    if (
      Math.abs(point.z + Math.sign(point.z) * (width / 2)) >
      Math.abs(roomBox.max.z)
    ) {
      return false;
    }
  }
  return true;
}

export function clampPointToRoomMesh(point, width, height, normal, roomMesh) {
  const roomBox = new Box3().setFromObject(roomMesh);
  const fixed = point.clone();
  const topLimit = roomBox.max.y - height / 2 - 0.01;
  const bottomLimit = roomBox.min.y + height / 2 + 0.01;
  fixed.y = Math.min(Math.max(fixed.y, bottomLimit), topLimit);

  if (Math.abs(normal.z) > Math.abs(normal.x)) {
    const maxX = roomBox.max.x - width / 2;
    const minX = roomBox.min.x + width / 2;
    fixed.x = Math.min(Math.max(fixed.x, minX), maxX);
  } else {
    const maxZ = roomBox.max.z - width / 2;
    const minZ = roomBox.min.z + width / 2;
    fixed.z = Math.min(Math.max(fixed.z, minZ), maxZ);
  }

  return fixed;
}

export function isValidAllowedLink(url) {
  try {
    const parsed = new URL(url);

    const allowedHosts = [
      "youtube.com",
      "www.youtube.com",
      "youtu.be",
      "instagram.com",
      "www.instagram.com",
      "spotify.com",
      "open.spotify.com",
    ];

    return allowedHosts.some((host) => parsed.hostname === host);
  } catch {
    return false; // not a valid URL at all
  }
}

export function handleZoomOnlyView(
  cameraControls,
  height,
  width = 50,
  depth = 50
) {
  if (!cameraControls) return;

  const camera = cameraControls.camera;
  const aspect = camera.aspect;

  // distance from camera to wall (on z axis)
  const distance = depth || 1;

  const vFovNeeded = 2 * Math.atan(height / (2 * distance));
  const hFovNeeded = 2 * Math.atan(width / (2 * distance));

  const fov = radToDeg(Math.max(vFovNeeded, hFovNeeded / aspect));

  camera.fov = fov;
  camera.updateProjectionMatrix();

  cameraControls.setTarget(0, height / 2, 0, true);
  cameraControls.setPosition(0, height / 2, distance, true);

  // Allow only zooming (disable rotation and panning)
  cameraControls.mouseButtons.left = CameraControlsImpl.ACTION.NONE; // Disable rotation
  cameraControls.mouseButtons.right = CameraControlsImpl.ACTION.NONE; // Disable panning
  cameraControls.mouseButtons.middle = CameraControlsImpl.ACTION.ZOOM; // Allow zoom

  // For touch controls
  cameraControls.touches.one = CameraControlsImpl.ACTION.NONE; // Disable touch rotation
  cameraControls.touches.two = CameraControlsImpl.ACTION.TOUCH_ZOOM; // Allow pinch zoom
  cameraControls.touches.three = CameraControlsImpl.ACTION.NONE; // Disable 3-finger gestures

  cameraControls.enabled = true; // Controls stay active
}

// Reset function to restore all controls
export function resetCameraControls(cameraControls, lockCameraForEditor) {
  if (cameraControls) {
    cameraControls.setTarget(0, 0, 0, true);
    cameraControls.setPosition(75, 75, 75, true);

    cameraControls.camera.fov = 60;
    cameraControls.camera.updateProjectionMatrix();
    // Restore default mouse buttons
    cameraControls.mouseButtons.left = CameraControlsImpl.ACTION.ROTATE;
    cameraControls.mouseButtons.right = CameraControlsImpl.ACTION.TRUCK;
    cameraControls.mouseButtons.middle = CameraControlsImpl.ACTION.ZOOM;

    // Restore default touch controls
    cameraControls.touches.one = CameraControlsImpl.ACTION.TOUCH_ROTATE;
    cameraControls.touches.two = CameraControlsImpl.ACTION.TOUCH_ZOOM_TRUCK;
    cameraControls.touches.three = CameraControlsImpl.ACTION.NONE;

    cameraControls.enabled = !lockCameraForEditor; // Ensure controls are active
  }
}

export function fitCameraToBox(cameraControls, { x, y, z }) {
  // create a temporary invisible mesh
  const geom = new BoxGeometry(x, y, z);
  const mat = new MeshBasicMaterial({ visible: false });
  const box = new Mesh(geom, mat);

  // position same as your <Box>
  box.position.set(0, y / 4 - 0.1, 0);
  box.updateMatrixWorld(true);

  // compute bounding sphere
  const sphere = new Sphere();
  geom.computeBoundingSphere();
  sphere.copy(geom.boundingSphere).applyMatrix4(box.matrixWorld);

  // fit camera to that sphere
  cameraControls.fitToSphere(sphere, true);

  // cleanup
  geom.dispose();
  mat.dispose();
}

/**
 * Determines which wall the camera is facing based on azimuth angle
 * and returns the appropriate position and rotation for placing a decoration.
 *
 * @param {object} cameraControls - The camera controls object with azimuthAngle
 * @param {number} scaleX - Room width
 * @param {number} scaleZ - Room depth
 * @param {number} height - Room height
 * @param {number} randomX - Random horizontal offset
 * @param {number} randomY - Random vertical offset
 * @returns {object} - { position: [x, y, z], rotation: [rx, ry, rz], wallName: string }
 */
export function getDecorationPlacementForCamera(cameraControls, scaleX, scaleZ, height, randomX = 0, randomY = 0) {
  // Default to back wall if no camera controls
  if (!cameraControls || typeof cameraControls.azimuthAngle !== 'number') {
    return {
      position: [randomX, height / 2 + randomY, -scaleZ / 2 + 0.5],
      rotation: [0, 0, 0],
      wallName: 'back'
    };
  }

  // Get azimuth angle (in radians)
  // azimuthAngle = 0: Camera on +Z side, looking at back wall (-Z)
  // azimuthAngle = π/2: Camera on -X side, looking at left wall (-X faces +X)
  // azimuthAngle = π or -π: Camera on -Z side, looking at front wall (+Z)
  // azimuthAngle = -π/2: Camera on +X side, looking at right wall (+X faces -X)
  let azimuth = cameraControls.azimuthAngle;

  // Normalize to -π to π range
  while (azimuth > Math.PI) azimuth -= 2 * Math.PI;
  while (azimuth < -Math.PI) azimuth += 2 * Math.PI;

  const PI = Math.PI;
  const QUARTER = PI / 4;

  // Determine which wall based on azimuth angle
  if (azimuth >= -QUARTER && azimuth < QUARTER) {
    // Back wall (negative Z, facing positive Z)
    return {
      position: [randomX, height / 2 + randomY, -scaleZ / 2 + 0.5],
      rotation: [0, 0, 0],
      wallName: 'back'
    };
  } else if (azimuth >= QUARTER && azimuth < 3 * QUARTER) {
    // Left wall (negative X, facing positive X)
    return {
      position: [-scaleX / 2 + 0.5, height / 2 + randomY, randomX],
      rotation: [0, PI / 2, 0],
      wallName: 'left'
    };
  } else if (azimuth >= 3 * QUARTER || azimuth < -3 * QUARTER) {
    // Front wall (positive Z, facing negative Z)
    return {
      position: [randomX, height / 2 + randomY, scaleZ / 2 - 0.5],
      rotation: [0, PI, 0],
      wallName: 'front'
    };
  } else {
    // Right wall (positive X, facing negative X)
    return {
      position: [scaleX / 2 - 0.5, height / 2 + randomY, randomX],
      rotation: [0, -PI / 2, 0],
      wallName: 'right'
    };
  }
}

export function getAlphaTexture(image) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  canvas.width = image.width;
  canvas.height = image.height;

  ctx.drawImage(image, 0, 0);
  const { data } = ctx.getImageData(0, 0, image.width, image.height);

  // Create grayscale alpha map
  const alphaData = new Uint8ClampedArray(image.width * image.height * 4);
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    alphaData[i] = alpha; // R
    alphaData[i + 1] = alpha; // G
    alphaData[i + 2] = alpha; // B
    alphaData[i + 3] = 255; // full opacity
  }

  const alphaCanvas = document.createElement("canvas");
  alphaCanvas.width = image.width;
  alphaCanvas.height = image.height;
  const alphaCtx = alphaCanvas.getContext("2d");
  const alphaImageData = new ImageData(alphaData, image.width, image.height);
  alphaCtx.putImageData(alphaImageData, 0, 0);

  const alphaTexture = new CanvasTexture(alphaCanvas);
  alphaTexture.colorSpace = SRGBColorSpace;
  return alphaTexture;
}
